import React, { useState } from 'react';
import { EDUCATION, ALL_TOOLS } from '../constants';
import { ABOUT_ASSETS } from '../assets';
import { usePlatformStats } from '../hooks/usePlatformStats'; 

const About: React.FC = () => {
  const [activeToolCategory, setActiveToolCategory] = useState('All');
  
  // Using the custom hook to get platform stats
  const { stats, formatNumber } = usePlatformStats();

  const categories = ['All', 'Language', 'Frontend', 'Backend & DB', 'Data & Auto', 'Cloud & DevOps', 'Design', 'IDE & Tools'];
  
  const filteredTools = activeToolCategory === 'All' 
    ? ALL_TOOLS 
    : ALL_TOOLS.filter(t => t.category === activeToolCategory);

  return (
    <div className="w-full max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12 flex flex-col gap-12 md:gap-20 animate-fade-in-up">
      {/* Hero Section */}
      <section className="flex flex-col md:flex-row gap-8 md:gap-12 items-center md:items-start">
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-primary to-orange-600 rounded-xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
          <div className="relative h-48 w-48 md:h-64 md:w-64 rounded-xl overflow-hidden shadow-2xl bg-surface-dark border border-border-dark">
            <div 
              className="w-full h-full bg-cover bg-center" 
              style={{ backgroundImage: `url(${ABOUT_ASSETS.developerprofile})` }}
            ></div>
          </div>
        </div>
        <div className="flex flex-col text-center md:text-left flex-1">
          <div className="inline-flex items-center justify-center md:justify-start gap-2 mb-2">
            <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider border border-primary/20">1st Year University Student</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight mb-4 tracking-tighter">
            Hello, I'm <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-500">Ridwan</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 text-lg md:text-xl leading-relaxed max-w-2xl">
            I am a first-year Information Systems student at UNNES driven by an obsession with how things work. My journey isn't a straight line; it crosses through <span className="font-bold"> data analysis, Android development, Roblox game development, and workflow automation</span>.
          </p>
          <p className="text-slate-600 dark:text-slate-400 text-lg md:text-xl leading-relaxed max-w-2xl mt-4">
            While my peers focus on theory, I focus on shipping. Whether it's optimizing a Kotlin algorithm for a calculator app or scripting complex mechanics in <span className="font-bold">Luau</span>, I build digital experiences that are functional, fun, and automated.
          </p>
        </div>
      </section>

      {/* Stats Grid */}
      <section className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { value: '1', label: 'Year Student' },
          { value: '4', label: 'Projects' },
          { value: '40+', label: 'Certifications' },
        ].map((stat, idx) => (
          <div key={idx} className="flex flex-col items-center justify-center p-6 rounded-xl bg-white dark:bg-surface-dark border border-gray-200 dark:border-border-dark shadow-sm hover:border-primary/50 transition-colors group">
            <span className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white group-hover:text-primary transition-colors">{stat.value}</span>
            <span className="text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest mt-1">{stat.label}</span>
          </div>
        ))}
      </section>

      {/* My Journey */}
      <section>
        <div className="flex items-center gap-4 mb-6">
          <div className="h-8 w-1 bg-primary rounded-full"></div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">My Journey</h2>
        </div>
        <div className="prose prose-lg dark:prose-invert max-w-none text-slate-600 dark:text-slate-300">
          <p className="mb-4">
            I first became interested in programming during the Covid-19 pandemic when I accidentally saw a video on my YouTube homepage about creating games in Roblox Studio. Because of that, I started exploring Roblox Studio and trying to learn Luau (it's difficult). Within a few months, I managed to create two simple games in Roblox Studio.
          </p>
          <p>
            Over the years, my interests have expanded beyond game development. I started learning about data analysis using Python and SQL, which led me to explore automation tools like n8n to streamline repetitive tasks. Additionally, I ventured into Android development with Kotlin, creating a calculator app that helped me understand mobile app development fundamentals. 
            Also, I am currently pursuing my studies in Information Systems at UNNES to deepen my understanding of technology and its applications in solving real-world problems.
          </p>
        </div>
      </section>

      {/* Educational History */}
      <section>
        <div className="flex items-center gap-4 mb-8">
          <div className="h-8 w-1 bg-primary rounded-full"></div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">Educational History</h2>
        </div>
        <div className="relative border-l-2 border-border-dark ml-3 md:ml-6 space-y-12 pb-4">
          {EDUCATION.map((edu) => (
            <div key={edu.id} className="relative pl-8 md:pl-12">
              <span className={`absolute -left-[9px] top-6 h-4 w-4 rounded-full border-2 bg-background-dark ${edu.color === 'border-primary' ? 'border-primary' : 'border-border-dark'}`}></span>
              
              <div className="flex flex-col sm:flex-row gap-5 items-start">
                {/* Logo Section */}
                <div className="shrink-0 p-1.5 bg-white dark:bg-card-dark rounded-xl border border-gray-200 dark:border-border-dark shadow-sm mt-1">
                  <img 
                    src={edu.logo} 
                    alt={`${edu.school} Logo`} 
                    className="size-16 md:size-20 object-contain rounded-lg" 
                  />
                </div>

                {/* Content Section */}
                <div className="flex-1 w-full">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">{edu.school}</h3>
                    <span className={`self-start sm:self-auto text-sm font-mono px-2 py-1 rounded whitespace-nowrap ${edu.color === 'border-primary' ? 'text-primary bg-primary/10' : 'text-slate-500 dark:text-slate-400 bg-gray-100 dark:bg-surface-dark'}`}>{edu.period}</span>
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 font-bold text-sm mb-3">{edu.degree}</p>
                  <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                    {edu.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Technical Skills - New Toolbox */}
      <section>
        <div className="flex items-center gap-4 mb-8">
          <div className="h-8 w-1 bg-primary rounded-full"></div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">Technical Skills</h2>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
           {categories.map((cat) => (
             <button
               key={cat}
               onClick={() => setActiveToolCategory(cat)}
               className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                 activeToolCategory === cat
                   ? 'bg-primary text-slate-900 shadow-lg shadow-primary/20'
                   : 'bg-gray-100 dark:bg-surface-dark text-slate-500 hover:text-slate-900 dark:hover:text-white'
               }`}
             >
               {cat}
             </button>
           ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
          {filteredTools.map((tool) => (
            <a 
              key={tool.name}
              href={tool.url}
              className={`group flex flex-col items-center justify-center p-4 rounded-xl bg-white dark:bg-card-dark border border-gray-100 dark:border-border-dark transition-all duration-300 hover:scale-105 hover:-translate-y-1 shadow-sm hover:shadow-lg ${tool.color}`}
            >
              <div className="size-10 mb-3 transition-transform duration-500 group-hover:scale-110">
                <img src={tool.logo} alt={tool.name} className="size-full group-hover:invert-0" />
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-400 group-hover:text-inherit text-center">{tool.name}</span>
            </a>
          ))}
        </div>
      </section>

      {/* Profile Dashboard - REAL TIME DATA */}
      <section id="profile-dashboard">
        <div className="flex items-center gap-4 mb-8">
          <div className="h-8 w-1 bg-primary rounded-full"></div>
          <h2 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white tracking-tight">Profile Dashboard</h2>
        </div>
        
        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          
          {/* 1. GitHub (AUTOMATED) */}
          <a href="https://github.com/Yogs4R" className="group relative overflow-hidden rounded-xl bg-[#0d1117] border border-[#30363d] hover:border-[#2ea44f] hover:shadow-[0_0_20px_rgba(46,164,79,0.2)] transition-all duration-300 active:scale-95 flex flex-col h-full">
            <div className="absolute top-0 left-0 w-full h-1 bg-[#2ea44f]"></div>
            <div className="p-5 flex flex-col h-full">
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-lg bg-white text-[#2ea44f] flex items-center justify-center group-hover:bg-[#2ea44f] group-hover:text-white transition-colors">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/9/91/Octicons-mark-github.svg" alt="GitHub" className="size-6" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-base">GitHub</h3>
                    <p className="text-xs text-gray-400 group-hover:text-[#2ea44f] transition-colors">@Yogs4R</p>
                  </div>
                </div>
                <span className="material-symbols-outlined text-gray-500 group-hover:text-[#2ea44f] transition-colors">north_east</span>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mt-auto">
                 <div className="bg-[#161b22] p-3 rounded-lg border border-[#30363d] group-hover:border-[#2ea44f]/30 transition-colors">
                    <div className="text-[10px] text-gray-400 font-bold uppercase mb-1">Repositories</div>
                    <div className="text-xl font-bold text-white">{stats.github?.repos ?? 0}</div>
                 </div>
                 <div className="bg-[#161b22] p-3 rounded-lg border border-[#30363d] group-hover:border-[#2ea44f]/30 transition-colors">
                    <div className="text-[10px] text-gray-400 font-bold uppercase mb-1">Contributions</div>
                    <div className="text-xl font-bold text-[#2ec866]">{formatNumber(stats.github?.contributions ?? 0)}</div>
                 </div>
                 <div className="bg-[#161b22] p-3 rounded-lg border border-[#30363d] group-hover:border-[#2ea44f]/30 transition-colors">
                    <div className="text-[10px] text-gray-400 font-bold uppercase mb-1">Followers</div>
                    <div className="text-xl font-bold text-white">{stats.github?.followers ?? 0}</div>
                 </div>
                 <div className="bg-[#161b22] p-3 rounded-lg border border-[#30363d] group-hover:border-[#2ea44f]/30 transition-colors">
                    <div className="text-[10px] text-gray-400 font-bold uppercase mb-1">Starred</div>
                    <div className="text-xl font-bold text-[#e3b341]">{stats.github?.stars ?? 0}</div>
                 </div>
              </div>
            </div>
          </a>

          {/* 2. LinkedIn (MANUAL - Database) */}
          <a href="https://www.linkedin.com/in/ridwansuryantara/" className="group relative overflow-hidden rounded-xl bg-[#1e293b] border border-[#334155] hover:border-[#0a66c2] hover:shadow-[0_0_20px_rgba(10,102,194,0.2)] transition-all duration-300 active:scale-95 flex flex-col h-full">
             <div className="absolute top-0 left-0 w-full h-1 bg-[#0a66c2]"></div>
             <div className="p-5 flex flex-col h-full">
                <div className="flex justify-between items-start mb-6">
                   <div className="flex items-center gap-3">
                      <div className="size-10 rounded-lg bg-[#0a66c2]/10 text-[#0a66c2] flex items-center justify-center group-hover:bg-[#0a66c2] group-hover:text-white transition-colors">
                         <img src="https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png" alt="LinkedIn" className="size-6" />
                      </div>
                      <div>
                         <h3 className="text-white font-bold text-base">LinkedIn</h3>
                         <p className="text-xs text-gray-400 group-hover:text-[#0a66c2] transition-colors">Professional Network</p>
                      </div>
                   </div>
                   <span className="material-symbols-outlined text-gray-500 group-hover:text-[#0a66c2] transition-colors">north_east</span>
                </div>
                
                <div className="mt-auto space-y-4">
                   <div className="flex items-center justify-between p-3 rounded-lg bg-[#0a66c2]/10 border border-[#0a66c2]/20">
                      <div className="flex items-center gap-2">
                         <span className="material-symbols-outlined text-[#0a66c2] text-lg">group</span>
                         <span className="text-xs font-bold text-white">Connections</span>
                      </div>
                      <span className="text-lg font-bold text-white">{formatNumber(stats.linkedin?.connections) ?? 0}</span>
                   </div>
                   <div className="grid grid-cols-2 gap-4 pt-2">
                      <div>
                         <div className="text-2xl font-bold text-white">{formatNumber(stats.linkedin?.views ?? 0)}</div>
                         <div className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">Profile Views</div>
                      </div>
                       <div>
                         <div className="text-2xl font-bold text-white">{formatNumber(stats.linkedin?.certifications ?? 0)}</div>
                         <div className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">Certifications</div>
                      </div>
                   </div>
                </div>
             </div>
          </a>

          {/* 3. Microsoft Learn (MANUAL - Database) */}
          <a href="https://learn.microsoft.com/id-id/users/riyogs/" className="group relative overflow-hidden rounded-xl bg-[#1e293b] border border-[#334155] hover:border-[#0078d4] hover:shadow-[0_0_20px_rgba(0,120,212,0.2)] transition-all duration-300 active:scale-95 flex flex-col h-full">
             <div className="absolute top-0 left-0 w-full h-1 bg-[#0078d4]"></div>
             <div className="p-5 flex flex-col h-full">
                <div className="flex justify-between items-start mb-6">
                   <div className="flex items-center gap-3">
                      <div className="size-10 rounded-lg bg-[#0078d4]/10 text-[#0078d4] flex items-center justify-center group-hover:bg-[#0078d4] group-hover:text-white transition-colors">
                         <img src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg" alt="Microsoft Learn" className="size-6" />
                      </div>
                      <div>
                         <h3 className="text-white font-bold text-base">Microsoft Learn</h3>
                         <p className="text-xs text-gray-400 group-hover:text-[#0078d4] transition-colors">Achievement Profile</p>
                      </div>
                   </div>
                   <span className="material-symbols-outlined text-gray-500 group-hover:text-[#0078d4] transition-colors">north_east</span>
                </div>
                
                <div className="flex flex-col gap-5 mt-auto">
                   <div className="flex items-center gap-4">
                      <div className="size-12 rounded-full border-4 border-[#0078d4] flex items-center justify-center text-[#0078d4] font-bold text-xs bg-[#0078d4]/10 group-hover:bg-[#0078d4] group-hover:text-white transition-colors">
                         Lvl {stats.microsoft_learn?.level ?? 0}
                      </div>
                      <div>
                          <div className="text-2xl font-bold text-white">{formatNumber(stats.microsoft_learn?.xp ?? 0)}</div>
                          <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Total XP Points</div>
                      </div>
                   </div>
                   <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-700/50">
                      <div className="text-center">
                        <div className="text-white font-bold text-lg">{stats.microsoft_learn?.badges ?? 0}</div>
                        <div className="text-[9px] text-gray-400 uppercase">Badges</div>
                      </div>
                      <div className="text-center">
                        {/* Manual field: certificates count must be updated in the data source */}
                        <div className="text-white font-bold text-lg">{stats.microsoft_learn?.certificates ?? 0}</div>
                        <div className="text-[9px] text-gray-400 uppercase">Certs</div>
                      </div>
                      <div className="text-center">
                        <div className="text-white font-bold text-lg">{stats.microsoft_learn?.trophies ?? 0}</div>
                        <div className="text-[9px] text-gray-400 uppercase">Trophies</div>
                      </div>
                   </div>
                </div>
             </div>
          </a>

          {/* 4. LeetCode (AUTOMATED) */}
          <a href="https://leetcode.com/u/riyogs/" className="group relative overflow-hidden rounded-xl bg-[#1e293b] border border-[#334155] hover:border-[#ffa116] hover:shadow-[0_0_20px_rgba(255,161,22,0.2)] transition-all duration-300 active:scale-95 flex flex-col h-full">
             <div className="absolute top-0 left-0 w-full h-1 bg-[#ffa116]"></div>
             <div className="p-5 flex flex-col h-full">
                <div className="flex justify-between items-start mb-6">
                   <div className="flex items-center gap-3">
                      <div className="size-10 rounded-lg bg-[#ffa116]/10 text-[#ffa116] flex items-center justify-center group-hover:bg-[#ffa116] group-hover:text-white transition-colors">
                         <img src="https://upload.wikimedia.org/wikipedia/commons/1/19/LeetCode_logo_black.png" alt="LeetCode" className="size-6" />
                      </div>
                      <div>
                         <h3 className="text-white font-bold text-base">LeetCode</h3>
                         <p className="text-xs text-gray-400 group-hover:text-[#ffa116] transition-colors">Problem Solving</p>
                      </div>
                   </div>
                   <span className="material-symbols-outlined text-gray-500 group-hover:text-[#ffa116] transition-colors">north_east</span>
                </div>
                
                <div className="grid grid-cols-3 gap-2 mt-auto">
                   <div className="bg-[#283245] p-2 rounded-lg text-center group-hover:bg-[#ffa116]/10 transition-colors">
                      <div className="text-[#00b8a3] font-bold text-lg">{stats.leetcode?.easy ?? 0}</div>
                      <div className="text-[9px] text-gray-400 uppercase font-bold">Easy</div>
                   </div>
                   <div className="bg-[#283245] p-2 rounded-lg text-center group-hover:bg-[#ffa116]/10 transition-colors">
                      <div className="text-[#ffc01e] font-bold text-lg">{stats.leetcode?.medium ?? 0}</div>
                      <div className="text-[9px] text-gray-400 uppercase font-bold">Med</div>
                   </div>
                   <div className="bg-[#283245] p-2 rounded-lg text-center group-hover:bg-[#ffa116]/10 transition-colors">
                      <div className="text-[#ff375f] font-bold text-lg">{stats.leetcode?.hard ?? 0}</div>
                      <div className="text-[9px] text-gray-400 uppercase font-bold">Hard</div>
                   </div>
                </div>
                <div className="text-center mt-3 pt-2 border-t border-gray-700/50">
                   <span className="text-gray-400 text-xs">Global Rank: <span className="text-white font-bold">{stats.leetcode?.ranking ? `#${formatNumber(stats.leetcode.ranking)}` : "-"}</span></span>
                </div>
             </div>
          </a>

          {/* 5. HackerRank (AUTOMATED) */}
          <a href="https://www.hackerrank.com/profile/ridwansuryantara" className="group relative overflow-hidden rounded-xl bg-[#1e293b] border border-[#334155] hover:border-[#2ec866] hover:shadow-[0_0_20px_rgba(46,200,102,0.2)] transition-all duration-300 active:scale-95 flex flex-col h-full">
             <div className="absolute top-0 left-0 w-full h-1 bg-[#2ec866]"></div>
             <div className="p-5 flex flex-col h-full">
                <div className="flex justify-between items-start mb-6">
                   <div className="flex items-center gap-3">
                      <div className="size-10 rounded-lg bg-[#181818] flex items-center justify-center">
                         <img src="https://upload.wikimedia.org/wikipedia/commons/4/40/HackerRank_Icon-1000px.png" alt="HackerRank" className="size-6" />
                      </div>
                      <div>
                         <h3 className="text-white font-bold text-base">HackerRank</h3>
                         <p className="text-xs text-gray-400 group-hover:text-[#2ec866] transition-colors">Coding Skills</p>
                      </div>
                   </div>
                   <span className="material-symbols-outlined text-gray-500 group-hover:text-[#2ec866] transition-colors">north_east</span>
                </div>
                
                <div className="flex items-end justify-between mt-auto">
                   <div>
                      <div className="text-2xl font-bold text-white mb-1">{stats.hackerrank?.highest_star ?? 1} Star</div>
                      <div className="text-xs text-gray-400">Problem Solving</div>
                      <div className="flex gap-2 mt-3">
                        {/* Use Keys from stars object if available, otherwise use default value */}
                        {stats.hackerrank?.stars ? Object.entries(stats.hackerrank.stars).sort(([, aStars], [, bStars]) => (bStars as number) - (aStars as number)).slice(0, 2).map(([lang, star]) => (
                            <span key={lang} className="px-2 py-1 rounded bg-[#2ec866]/20 text-[#2ec866] text-[9px] font-bold">{lang} ({star}★)</span>
                        )) : (
                          <>
                            <span className="px-2 py-1 rounded bg-[#2ec866]/20 text-[#2ec866] text-[9px] font-bold">Python (Gold★)</span>
                            <span className="px-2 py-1 rounded bg-[#2ec866]/20 text-[#2ec866] text-[9px] font-bold">SQL (Gold★)</span>
                          </>
                        )}
                      </div>
                   </div>
                   <div className="size-10 rounded-full border-2 border-[#2ec866] flex items-center justify-center text-[#2ec866] bg-[#2ec866]/5 group-hover:bg-[#2ec866] group-hover:text-white transition-colors mb-2">
                      <span className="material-symbols-outlined text-lg">star</span>
                   </div>
                </div>
             </div>
          </a>

          {/* 6. Google Developer (MANUAL - Database) */}
           <a href="https://g.dev/ridwansuryantara" className="group relative overflow-hidden rounded-xl bg-[#1e293b] border border-[#334155] hover:border-[#4285F4] hover:shadow-[0_0_20px_rgba(66,133,244,0.2)] transition-all duration-300 active:scale-95 flex flex-col h-full">
             <div className="absolute top-0 left-0 w-full h-1 bg-[#4285F4]"></div>
             <div className="p-5 flex flex-col h-full">
                <div className="flex justify-between items-start mb-6">
                   <div className="flex items-center gap-3">
                      <div className="size-10 rounded-lg bg-white flex items-center justify-center">
                         <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg" className="size-6" alt="Google" />
                      </div>
                      <div>
                         <h3 className="text-white font-bold text-base">Google Dev</h3>
                         <p className="text-xs text-gray-400 group-hover:text-[#4285F4] transition-colors">Badges Profile</p>
                      </div>
                   </div>
                   <span className="material-symbols-outlined text-gray-500 group-hover:text-[#4285F4] transition-colors">north_east</span>
                </div>
                
                <div className="mt-auto">
                   <div className="flex items-end gap-2 mb-2">
                      <span className="text-3xl font-bold text-white">{stats.google_dev?.total_badges ?? 0}</span>
                      <span className="text-xs text-gray-400 uppercase font-bold mb-1">Total Badges</span>
                   </div>
                   <div className="w-full h-1.5 bg-[#283245] rounded-full overflow-hidden mb-4">
                      <div className="h-full bg-gradient-to-r from-[#4285F4] via-[#EA4335] to-[#FBBC05] transition-all duration-1000 ease-out" style={{ width: `${Math.min(((stats.google_dev?.total_badges ?? 0) / 50) * 100, 100)}%` }}></div>
                   </div>
                   <div className="flex items-center gap-2 text-gray-400 pt-3 border-t border-gray-700/50">
                      <span className="material-symbols-outlined text-sm">groups</span>
                      <span className="text-[9px] font-medium uppercase tracking-wide">Member Google Developer Group Semarang</span>
                   </div>
                </div>
             </div>
          </a>

           {/* 7. FreeCodeCamp (MANUAL - Database) */}
           <a href="https://www.freecodecamp.org/ridwansuryantara" className="group relative overflow-hidden rounded-xl bg-[#1e293b] border border-[#334155] hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all duration-300 active:scale-95 flex flex-col h-full">
             <div className="absolute top-0 left-0 w-full h-1 bg-white"></div>
             <div className="p-5 flex flex-col h-full">
                <div className="flex justify-between items-start mb-6">
                   <div className="flex items-center gap-3">
                      <img src="https://raw.githubusercontent.com/simple-icons/simple-icons/develop/icons/freecodecamp.svg" className="size-8 invert" alt="fCC" />
                      <h3 className="text-white font-bold text-base">freeCodeCamp</h3>
                   </div>
                   <span className="material-symbols-outlined text-gray-500 group-hover:text-white transition-colors" aria-hidden="true">north_east</span>
                </div>
                <div className="mt-auto">
                   <div className="flex items-baseline gap-2 mb-3">
                      <span className="text-3xl font-bold text-white">{stats.freecodecamp?.certifications ?? 0}</span>
                      <span className="text-sm text-gray-400 font-medium group-hover:text-white transition-colors">Certifications</span>
                   </div>
                   <div className="w-full h-1.5 bg-[#283245] rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 transition-all duration-1000 ease-out" style={{ width: `${Math.min(((stats.freecodecamp?.certifications ?? 0) / 10) * 100, 100)}%` }}></div>
                   </div>
                </div>
             </div>
          </a>

           {/* 8. Roblox Studio (AUTOMATED) */}
          <a href="https://create.roblox.com/talent/creators/2253933194" className="group relative overflow-hidden rounded-xl bg-[#1e293b] border border-[#334155] hover:border-[#E2231A] hover:shadow-[0_0_20px_rgba(226,35,26,0.2)] transition-all duration-300 active:scale-95 flex flex-col h-full">
             <div className="absolute top-0 left-0 w-full h-1 bg-[#E2231A]"></div>
             <div className="p-5 flex flex-col h-full">
                <div className="flex justify-between items-start mb-6">
                   <div className="flex items-center gap-3">
                      <div className="size-10 rounded-lg bg-white flex items-center justify-center">
                         <img src="https://upload.wikimedia.org/wikipedia/commons/e/eb/Roblox_Studio_logo_-_2022.svg" className="size-6" alt="Roblox" />
                      </div>
                      <div>
                         <h3 className="text-white font-bold text-base">Roblox Studio</h3>
                         <p className="text-xs text-gray-400 group-hover:text-[#E2231A] transition-colors">Developer</p>
                      </div>
                   </div>
                   <span className="material-symbols-outlined text-gray-500 group-hover:text-[#E2231A] transition-colors">north_east</span>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mt-auto">
                   <div className="flex flex-col items-center justify-center bg-[#E2231A]/10 p-3 rounded-lg border border-[#E2231A]/20 group-hover:bg-[#E2231A] group-hover:text-white transition-colors">
                      <div className="text-xl font-bold text-[#E2231A] group-hover:text-white">{formatNumber(stats.roblox?.total_visits ?? 0)}</div>
                      <div className="text-[9px] text-gray-400 uppercase font-bold group-hover:text-white/80">Visits</div>
                   </div>
                   <div className="flex flex-col items-center justify-center bg-[#E2231A]/10 p-3 rounded-lg border border-[#E2231A]/20 group-hover:bg-[#E2231A] group-hover:text-white transition-colors">
                      <div className="text-xl font-bold text-[#E2231A] group-hover:text-white">{stats.roblox?.total_games ?? 0}</div>
                      <div className="text-[9px] text-gray-400 uppercase font-bold group-hover:text-white/80">Games</div>
                   </div>
                </div>
             </div>
          </a>

          {/* 9. CognitiveClass (MANUAL + New Field Certification) */}
          <a href="https://cognitiveclass.ai/profile/Yogs4R" className="group relative overflow-hidden rounded-xl bg-[#1e293b] border border-[#334155] hover:border-[#137fec] hover:shadow-[0_0_20px_rgba(19,127,236,0.2)] transition-all duration-300 active:scale-95 flex flex-col h-full">
             <div className="absolute top-0 left-0 w-full h-1 bg-[#137fec]"></div>
             <div className="p-5 flex flex-col h-full">
                <div className="flex justify-between items-start mb-6">
                   <div className="flex items-center gap-3">
                      <div className="size-10 rounded-lg bg-white text-white flex items-center justify-center font-bold text-xs border border-white/20">
                        <img src={ABOUT_ASSETS.cognitiveclasslogo} alt="CognitiveClass" className="size-6" />
                      </div>
                      <div>
                         <h3 className="text-white font-bold text-base">CognitiveClass</h3>
                         <p className="text-xs text-gray-400 group-hover:text-[#137fec] transition-colors">IBM Training</p>
                      </div>
                   </div>
                   <span className="material-symbols-outlined text-gray-500 group-hover:text-[#137fec] transition-colors">north_east</span>
                </div>
                
                <div className="mt-auto">
                   <div className="flex items-center justify-between mb-4">
                      <div className="grid grid-cols-2 gap-4 w-full">
                        <div className="text-center">
                         <div className="text-3xl font-black text-white">{stats.cognitive_class?.badges ?? 0}</div>
                         <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Badges Earned</div>
                      </div>
                      <div className="text-center">
                           <div className="text-3xl font-black text-white">{stats.cognitive_class?.certifications ?? 0}</div>
                           <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Certifications</div>
                      </div>
                      </div>
                   </div>
                   <div className="w-full h-1.5 bg-[#283245] rounded-full overflow-hidden">
                      <div className="h-full bg-[#137fec] transition-all duration-1000 ease-out" style={{ width: `${Math.min(((stats.cognitive_class?.badges ?? 0) / 50) * 100, 100)}%` }}></div>
                   </div>
                   <div className="flex justify-between mt-2 text-[9px] text-gray-400 uppercase font-bold">
                      <span>Progress</span>
                      <span className="text-[#137fec]">{stats.cognitive_class?.progress || 'Top 10%'}</span>
                   </div>
                </div>
             </div>
          </a>

           {/* 10. SoloLearn (AUTOMATED) */}
          <a href="https://www.sololearn.com/en/profile/34686483" className="group relative overflow-hidden rounded-xl bg-[#1e293b] border border-[#334155] hover:border-[#10C8FF] hover:shadow-[0_0_20px_rgba(16,200,255,0.2)] transition-all duration-300 active:scale-95 flex flex-col h-full">
             <div className="absolute top-0 left-0 w-full h-1 bg-[#10C8FF]"></div>
             <div className="p-5 flex flex-col h-full">
                <div className="flex justify-between items-start mb-6">
                   <div className="flex items-center gap-3">
                      <div className="size-10 rounded-lg bg-white flex items-center justify-center">
                         <img src="https://raw.githubusercontent.com/simple-icons/simple-icons/develop/icons/sololearn.svg" className="size-6" alt="SoloLearn" />
                      </div>
                      <div>
                         <h3 className="text-white font-bold text-base">SoloLearn</h3>
                         <p className="text-xs text-gray-400 group-hover:text-[#10C8FF] transition-colors">Skill Mastery</p>
                      </div>
                   </div>
                   <span className="material-symbols-outlined text-gray-500 group-hover:text-[#10C8FF] transition-colors">north_east</span>
                </div>
                
                <div className="mt-auto">
                   <div className="flex items-end justify-between mb-2">
                      <span className="text-3xl font-bold text-white">Lvl {stats.sololearn?.level ?? 0}</span>
                      <span className="text-xs text-[#10C8FF] font-bold group-hover:text-white transition-colors">{formatNumber(stats.sololearn?.xp ?? 0)} XP</span>
                   </div>
                   <div className="w-full h-1.5 bg-[#283245] rounded-full overflow-hidden mb-3">
                      <div className="h-full bg-[#10C8FF] transition-all duration-1000 ease-out" style={{ width: `${Math.min(((stats.sololearn?.xp ?? 0) / 20000) * 100, 100)}%` }}></div>
                   </div>
                   <div className="flex items-center justify-between text-[9px] text-gray-400 uppercase font-bold tracking-wider pt-2 border-t border-gray-700/50">
                      <span>Certificates</span>
                      <span className="text-white text-sm">{stats.sololearn?.certificates ?? 0}</span>
                   </div>
                </div>
             </div>
          </a>

          {/* 11. Coursera (MANUAL - Database) */}
          <a href="https://www.coursera.org/learner/ridwansuryantara" className="group relative overflow-hidden rounded-xl bg-[#1e293b] border border-[#334155] hover:border-[#0056d2] hover:shadow-[0_0_20px_rgba(0,86,210,0.2)] transition-all duration-300 active:scale-95 flex flex-col h-full">
             <div className="absolute top-0 left-0 w-full h-1 bg-[#0056d2]"></div>
             <div className="p-5 flex flex-col h-full">
                <div className="flex justify-between items-start mb-6">
                   <div className="flex items-center gap-3">
                      <div className="size-10 rounded-lg bg-white flex items-center justify-center">
                         <img src="https://upload.wikimedia.org/wikipedia/commons/2/21/Coursera-logo_500x500.svg" alt="Coursera" className="size-6" />
                      </div>
                      <div>
                         <h3 className="text-white font-bold text-base">Coursera</h3>
                         <p className="text-xs text-gray-400 group-hover:text-[#0056d2] transition-colors">Learning</p>
                      </div>
                   </div>
                   <span className="material-symbols-outlined text-gray-500 group-hover:text-[#0056d2] transition-colors">north_east</span>
                </div>
                
                <div className="space-y-4 mt-auto">
                   <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">Certifications</span>
                      <span className="text-white font-bold">{stats.coursera?.certifications ?? 0}</span>
                   </div>
                   <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">Learning Hours</span>
                      <span className="text-white font-bold">{stats.coursera?.hours ?? 0}+</span>
                   </div>
                   <div className="w-full h-1.5 bg-[#283245] rounded-full overflow-hidden">
                     {/* Max hours considered for progress bar is 500 hours */}
                      <div className="h-full bg-[#0056d2] transition-all duration-1000 ease-out" style={{ width: `${Math.min(((stats.coursera?.hours ?? 0) / 500) * 100, 100)}%` }}></div>
                   </div>
                </div>
             </div>
          </a>

           {/* 12. Dicoding (AUTOMATED) */}
          <a href="https://www.dicoding.com/users/riyogs/academies" className="group relative overflow-hidden rounded-xl bg-[#1e293b] border border-[#334155] hover:border-white hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all duration-300 active:scale-95 flex flex-col h-full">
             <div className="absolute top-0 left-0 w-full h-1 bg-[#2d3e50]"></div>
             <div className="p-5 flex flex-col h-full">
                <div className="flex justify-between items-start mb-6">
                   <div className="flex items-center gap-3">
                      <div className="size-10 rounded-lg bg-[#2d3e50] text-white flex items-center justify-center font-bold border border-white/20">
                        <img src={ABOUT_ASSETS.dicodinglogo} alt="Dicoding" className="size-6" />
                      </div>
                      <div>
                         <h3 className="text-white font-bold text-base">Dicoding</h3>
                         <p className="text-xs text-gray-400 group-hover:text-white transition-colors">Academy Learner</p>
                      </div>
                   </div>
                   <span className="material-symbols-outlined text-gray-500 group-hover:text-white transition-colors">north_east</span>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mt-auto">
                   <div className="bg-[#283245] p-3 rounded-lg text-center group-hover:bg-[#2d3e50] transition-colors">
                      <div className="text-white font-bold text-xl">{stats.dicoding?.classes ?? 0}</div>
                      <div className="text-[10px] text-gray-400 uppercase font-bold">Classes</div>
                   </div>
                   <div className="bg-[#283245] p-3 rounded-lg text-center group-hover:bg-[#2d3e50] transition-colors">
                      <div className="text-white font-bold text-xl">{formatNumber(stats.dicoding?.xp ?? 0)}</div>
                      <div className="text-[10px] text-gray-400 uppercase font-bold">XP</div>
                   </div>
                </div>
             </div>
          </a>

        </div>
      </section>
    </div>
  );
};

export default About;